import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { type EnumOption } from "./FilterDefinition";
import { type Filter, isValueless } from "./FilterSchema";

dayjs.extend(utc);

const DEFAULT_VALUE_LIMIT = 2;
const DATE_FORMAT = "M/D/YYYY";

export interface FormatFilterValueOptions {
  /**
   * Max values rendered before collapsing into "+N more". Applies to every
   * multi-value type. Date BETWEEN (always two values by contract) is unaffected.
   */
  valueLimit?: number;
  /**
   * Pre-resolved option list for the enum filter being formatted, when
   * the filter's definition supplies labeled options. The helper looks
   * each wire ``value`` up in this list and displays the matching
   * ``label`` instead. The caller is responsible for resolving lazy
   * (function-form) option providers before calling; this helper does
   * not await — for labeled enums whose options have not been resolved
   * yet, the chip renders the wire value verbatim and never updates.
   */
  enumOptions?: EnumOption[];
}

/**
 * The visible parts of a chip's value segment. The chip renders `text` and,
 * if present, a softer "+N more" tail. The fields are kept separate so the
 * tail can be styled distinctly without the helper returning React nodes.
 */
export interface FormattedFilterValue {
  /** Primary visible text — e.g. `"foo, bar"`, `"3/3/2025 → 3/5/2025"`. */
  text: string;
  /**
   * Count of remaining multi-values collapsed past `valueLimit`. Omitted
   * when nothing overflows or for non-multi-value types.
   */
  more?: number;
}

/**
 * Render the value-segment payload for a chip in `DataTable.ActiveFilters`.
 * Returns `null` when the comparator carries the full semantic on its own
 * (EXISTS / NOT_EXISTS / date presets) — callers should omit the value
 * segment entirely in that case.
 *
 * Inputs are assumed valid per `filterSchema`'s refine — empty arrays for
 * non-valueless comparators, malformed BETWEEN ranges, and NaN/Infinity
 * for numerics are rejected upstream by the per-type value inputs and the
 * schema refine. This helper does not re-validate.
 */
export function formatFilterValue(
  filter: Filter,
  opts: FormatFilterValueOptions = {},
): FormattedFilterValue | null {
  if (isValueless(filter.comparator)) {
    return null;
  }
  const limit = opts.valueLimit ?? DEFAULT_VALUE_LIMIT;

  switch (filter.type) {
    case "string":
      return joinWithLimit(filter.values, limit);
    case "numeric":
      return joinWithLimit(filter.values.map(String), limit);
    case "enum": {
      const labels = filter.values.map((v) =>
        resolveEnumLabel(v, opts.enumOptions),
      );
      return joinWithLimit(labels, limit);
    }
    case "boolean":
      // Schema's superRefine guarantees exactly one value for EQUALS
      // (EXISTS/NOT_EXISTS short-circuit above via `isValueless`).
      return { text: filter.values[0] ? "true" : "false" };
    case "date":
      if (filter.comparator === "BETWEEN") {
        return {
          text: `${formatDate(filter.values[0])} → ${formatDate(filter.values[1])}`,
        };
      }
      return { text: formatDate(filter.values[0]) };
  }
}

function joinWithLimit(parts: string[], limit: number): FormattedFilterValue {
  if (parts.length > limit) {
    return {
      text: parts.slice(0, limit).join(", "),
      more: parts.length - limit,
    };
  }
  return { text: parts.join(", ") };
}

// Dates are stored as ISO 8601 UTC-midnight strings (the date input's
// convention). Parsing + formatting in UTC keeps the calendar day stable
// regardless of the viewer's local timezone.
function formatDate(iso: string): string {
  return dayjs.utc(iso).format(DATE_FORMAT);
}

// For labeled enum filters, look the wire ``value`` up in the option
// list and return its ``label``. Falls back to the raw value when the
// option list is absent or doesn't contain a matching entry — the
// no-match case happens when the active filter references an option no
// longer in the option set (e.g. deleted resource, stale URL state).
// Rendering the opaque id beats blanking the chip.
function resolveEnumLabel(value: string, options?: EnumOption[]): string {
  if (options === undefined) {
    return value;
  }
  for (const option of options) {
    if (typeof option === "string") {
      if (option === value) {
        return option;
      }
    } else if (option.value === value) {
      return option.label;
    }
  }
  return value;
}
